import React, { useState, useEffect, useCallback } from 'react';
import { Activity, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { hardwareHistoryAPI } from '../../services/api';

// Customer Uptime Report (backend services/uptimeService): how much of a period the customer
// actually had internet, and what caused each outage. Collapsed by default; loads on open.

const CAUSE_COLORS = {
  olt_down: 'var(--cl-b91c1c)', pon_down: 'var(--cl-dc2626)', uplink_down: 'var(--cl-f87171)', onu_deactivated: 'var(--cl-fb923c)',
  power_off: 'var(--cl-6b7280)', fibre_loss: 'var(--cl-d97706)', onu_other: 'var(--cl-a78bfa)', session_down: 'var(--cl-2563eb)',
};
const SIDE_TEXT = { ours: 'our side', customer: 'customer side', line: 'fibre line', unknown: '' };

const PERIOD_SOURCE = {
  last_renewal: 'from the last renewal',
  plan_validity: 'from the plan validity',
  last_renewal_mismatch: 'from the last renewal — differs from current expiry',
};

const fmtMin = m => {
  if (m == null) return '—';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  if (h < 48) return `${h}h ${r}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};
const fmtTime = t => new Date(t).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const fmtPct = p => (p == null ? '—' : `${p}%`);
const LOG_COLORS = { alarm: 'var(--c-dc2626)', alarm_open: 'var(--c-dc2626)', alarm_resolved: 'var(--c-059669)' };
const toInputDate = d => new Date(d).toISOString().slice(0, 10);

const PRESETS = [
  { key: 'plan', label: 'Plan period' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: 'custom', label: 'Custom' },
];

export default function UptimePanel({ customerId, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [preset, setPreset] = useState('plan');
  const [custom, setCustom] = useState({ from: toInputDate(Date.now() - 7 * 86400e3), to: toInputDate(Date.now()) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openOutage, setOpenOutage] = useState(null);

  const load = useCallback(() => {
    let params = {};
    if (preset === '7d' || preset === '30d') {
      params = { from: new Date(Date.now() - (preset === '7d' ? 7 : 30) * 86400e3).toISOString() };
    } else if (preset === 'custom') {
      params = { from: new Date(`${custom.from}T00:00:00+05:30`).toISOString(), to: new Date(`${custom.to}T23:59:59+05:30`).toISOString() };
    }
    setLoading(true);
    setError(null);
    hardwareHistoryAPI.getUptime(customerId, params)
      .then(({ data: res }) => { setData(res.data); setOpenOutage(null); })
      .catch(err => setError(err.response?.data?.message || 'Could not load uptime'))
      .finally(() => setLoading(false));
  }, [customerId, preset, custom]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const causes = data?.causes || {};
  const downEntries = Object.entries(data?.downBy || {}).sort((a, b) => b[1] - a[1]);
  const pct = data?.uptimePct;

  return (
    <div style={{ background: 'var(--cb-ffffff)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '.88rem', color: 'var(--c-374151)', font: 'inherit' }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Activity size={14} color="var(--accent)" /> Uptime
        <span style={{ fontWeight: 400, color: 'var(--c-9ca3af)', fontSize: '.78rem' }}>how long they had internet, and why not</span>
      </button>

      {open && (
        <div style={{ borderTop: '1px solid var(--cb-e5e7eb)', padding: '12px 20px 16px' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            {PRESETS.map(p => (
              <button
                key={p.key} type="button" onClick={() => setPreset(p.key)}
                style={{
                  font: 'inherit', fontSize: '.78rem', padding: '4px 12px', borderRadius: 14, cursor: 'pointer', fontWeight: 600,
                  border: `1.5px solid ${preset === p.key ? 'var(--primary)' : 'var(--cb-e5e7eb)'}`,
                  background: preset === p.key ? 'var(--primary)' : 'var(--cb-ffffff)', color: preset === p.key ? 'var(--c-ffffff)' : 'var(--c-374151)',
                }}
              >{p.label}</button>
            ))}
            {preset === 'custom' && (
              <>
                <input type="date" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} style={{ font: 'inherit', fontSize: '.8rem' }} />
                <span style={{ color: 'var(--c-9ca3af)' }}>→</span>
                <input type="date" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} style={{ font: 'inherit', fontSize: '.8rem' }} />
              </>
            )}
            {loading && <Loader2 size={16} style={{ animation: 'spin .7s linear infinite' }} />}
          </div>

          {error && <div style={{ color: 'var(--c-dc2626)', fontSize: '.85rem' }}>{error}</div>}

          {data && !error && (
            <>
              <div style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', marginBottom: 8 }}>
                {fmtTime(data.from)} → {fmtTime(data.to)}
                {data.mode === 'plan' && data.periodSource !== 'fallback_30d' && ` · plan period ${data.periodMinutes.toLocaleString('en-IN')} min (ends ${fmtTime(data.periodEnd)}${PERIOD_SOURCE[data.periodSource] ? `, ${PERIOD_SOURCE[data.periodSource]}` : ''})`}
                {data.periodSource === 'fallback_30d' && ' · no plan period found — showing the last 30 days'}
                {data.recordingSince && ` · measured since ${fmtTime(data.recordingSince)}`}
              </div>

              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: '1.9rem', fontWeight: 800, color: pct == null ? 'var(--c-9ca3af)' : pct >= 99 ? 'var(--c-059669)' : pct >= 95 ? 'var(--c-d97706)' : 'var(--c-dc2626)' }}>
                    {pct == null ? '—' : `${pct}%`}
                  </div>
                  <div style={{ fontSize: '.72rem', color: 'var(--c-6b7280)', textTransform: 'uppercase' }}>up</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.9rem', fontWeight: 800, color: data.downtimePct == null ? 'var(--c-9ca3af)' : data.downtimePct > 0 ? 'var(--c-dc2626)' : 'var(--c-059669)' }}>
                    {fmtPct(data.downtimePct)}
                  </div>
                  <div style={{ fontSize: '.72rem', color: 'var(--c-6b7280)', textTransform: 'uppercase' }}>down</div>
                </div>
                <div style={{ fontSize: '.82rem', lineHeight: 1.7 }}>
                  <div><strong style={{ color: 'var(--c-059669)' }}>{data.upMinutes.toLocaleString('en-IN')} min</strong> up</div>
                  <div><strong style={{ color: 'var(--c-dc2626)' }}>{data.downMinutes.toLocaleString('en-IN')} min</strong> down ({fmtMin(data.downMinutes)})</div>
                  <div style={{ color: 'var(--c-6b7280)' }}>
                    {(data.elapsedMinutes - data.measuredMinutes).toLocaleString('en-IN')} min not measured
                    {Object.keys(data.excluded || {}).length > 0 && ` — ${Object.entries(data.excluded).map(([k, v]) => `${data.excludedLabels[k]}: ${fmtMin(v)}`).join(', ')}`}
                  </div>
                </div>
              </div>

              {pct == null && (
                <div style={{ color: 'var(--c-9ca3af)', fontSize: '.85rem', marginBottom: 8 }}>
                  Nothing measured in this period yet — recording checks every minute from today.
                </div>
              )}

              {downEntries.length > 0 && (
                <>
                  <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', marginBottom: 8, background: 'var(--cb-f3f4f6)' }}>
                    {downEntries.map(([k, v]) => (
                      <div key={k} title={`${causes[k]?.label}: ${fmtMin(v)}`} style={{ width: `${(v / data.downMinutes) * 100}%`, background: CAUSE_COLORS[k] || 'var(--cb-9ca3af)' }} />
                    ))}
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem', marginBottom: 12 }}>
                    <thead>
                      <tr style={{ color: 'var(--c-6b7280)', textAlign: 'left' }}>
                        <th style={{ fontWeight: 600, padding: '2px 0' }}>Down because of</th>
                        <th style={{ fontWeight: 600, textAlign: 'right' }}>Time</th>
                        <th style={{ fontWeight: 600, textAlign: 'right' }}>% of downtime</th>
                        <th style={{ fontWeight: 600, textAlign: 'right' }}>% of period</th>
                      </tr>
                    </thead>
                    <tbody>
                      {downEntries.map(([k, v]) => (
                        <tr key={k} style={{ borderTop: '1px solid var(--cb-f3f4f6)' }}>
                          <td style={{ padding: '3px 0' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 10, height: 10, borderRadius: 2, background: CAUSE_COLORS[k] || 'var(--cb-9ca3af)' }} />
                              {causes[k]?.label}
                              {SIDE_TEXT[causes[k]?.side] && <span style={{ color: 'var(--c-9ca3af)' }}>({SIDE_TEXT[causes[k].side]})</span>}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMin(v)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtPct(data.downPctBy?.[k]?.ofDown)}</td>
                          <td style={{ textAlign: 'right', color: 'var(--c-6b7280)' }}>{fmtPct(data.downPctBy?.[k]?.ofMeasured)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-6b7280)', margin: '6px 0' }}>Outages</div>
              {data.outages.length === 0 ? (
                <div style={{ color: 'var(--c-9ca3af)', fontSize: '.82rem' }}>No outages in this period.</div>
              ) : (
                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {data.outages.map((o, i) => {
                    const isOpen = openOutage === i;
                    return (
                      <div key={i} style={{ borderTop: i ? '1px solid var(--cb-f3f4f6)' : 'none' }}>
                        <button
                          type="button" onClick={() => setOpenOutage(isOpen ? null : i)}
                          style={{ width: '100%', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', fontSize: '.8rem', padding: '4px 0', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left', color: 'inherit' }}
                        >
                          {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          <span style={{ color: 'var(--c-6b7280)', minWidth: 220 }}>{fmtTime(o.start)} → {o.ongoing ? 'now' : fmtTime(o.end)}</span>
                          <span style={{ fontWeight: 700, minWidth: 70 }}>{fmtMin(Math.round(o.minutes))}</span>
                          <span style={{ color: 'var(--c-6b7280)', minWidth: 90 }}>{fmtPct(o.pctOfDown)} of down</span>
                          <span style={{ color: CAUSE_COLORS[o.cause] || 'var(--c-374151)', fontWeight: 600 }}>{causes[o.cause]?.label || o.cause}</span>
                        </button>
                        {isOpen && (
                          <div style={{ margin: '2px 0 8px 24px', padding: '6px 10px', background: 'var(--cb-f9fafb)', borderRadius: 6, fontSize: '.78rem' }}>
                            {(o.log || []).length === 0 ? (
                              <div style={{ color: 'var(--c-9ca3af)' }}>No log entries for this outage.</div>
                            ) : o.log.map((l, j) => (
                              <div key={j} style={{ display: 'flex', gap: 10, padding: '2px 0' }}>
                                <span style={{ color: 'var(--c-6b7280)', minWidth: 110 }}>{l.at ? fmtTime(l.at) : '—'}</span>
                                <span style={{ color: LOG_COLORS[l.kind] || 'var(--c-374151)' }}>{l.text}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
